import { portalColors } from '../Publications/publicationModel';
// frontend/src/components/Map/MapPreview.jsx
import React, { useEffect, useState } from "react";
import { GoogleMap, Marker, Polygon, useJsApiLoader } from "@react-google-maps/api";
import { Button } from "primereact/button";
import { generateCircleCoords } from "./utils/mapUtils";
import { useDrawingMode, polygonToPath } from "./utils/drawingUtils";

const mapContainerStyle = { width: "100%", height: "360px" };
const mapContainerStyleEmbedded = { width: "100%", height: "100%" };
const LIBRARIES = ["places", "drawing", "geometry"];

const DEFAULT_RADIUS = 500;
const MIN_RADIUS = 200;
const MAX_RADIUS = 1500;

const POLYGON_PRECISION_TIP =
  "Podés definir una o más zonas de búsqueda. Delimitá únicamente el área de interés para obtener resultados más precisos.";

/**
 * Preview modal del wizard.
 *
 * Flujo UX:
 *  1) Elección inicial explícita (botonera grande): "Por radio" vs "Dibujar zonas".
 *  2) En modo polygons: el usuario puede dibujar cuantos polígonos quiera.
 *     Los polígonos incompletos (< 3 puntos) se descartan; los válidos
 *     se auto-cierran.
 *  3) Si ya dibujó y cambia a modo radio: los polígonos quedan visibles pero
 *     no se usan para buscar. "Dibujar más" queda deshabilitado hasta que
 *     haga "Limpiar todo" (evita bugs de back-and-forth).
 *
 * Props:
 *  - center, radius, typeProperty, operationType, apiUrl
 *  - searchMode: 'radius' | 'polygons'
 *  - polygons: Array<Array<{lat,lng}>>
 *  - onModeChange(mode), onPolygonsChange(paths), onRadiusChange(m)
 *  - onClose(), onCountChange(count)
 *  - embedded: si true, se renderiza inline en el wizard (sin modal ni Aceptar)
 */
export default function MapPreview({
  center,
  radius = DEFAULT_RADIUS,
  typeProperty,
  operationType,
  apiUrl,
  searchMode = "radius",
  polygons = [],
  onModeChange,
  onPolygonsChange,
  onRadiusChange,
  onClose,
  onCountChange,
  embedded = false,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const [map, setMap] = useState(null);
  const [circlePath, setCirclePath] = useState([]);
  const [count, setCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [propertiesList, setPropertiesList] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [isDrawingMode, setIsDrawingMode] = useState(false);

  // Flag derivado: hay polígonos dibujados (visibles), independientemente del modo.
  const hasPolygons = polygons.length > 0;
  // No permitimos "dibujar más" si el usuario ya cambió a radio (evita bugs).
  const canDrawMore = searchMode === "polygons";

  // --- Dibujo: al completar un polígono, lo acumulamos como path plano y
  // quitamos la instancia que quedó en el mapa (el render lo hace <Polygon/>).
  const handlePolygonComplete = (polygon) => {
    const path = polygonToPath(polygon);
    polygon.setMap(null);
    if (path.length < 3) return; // polígono inválido → descartar silencioso
    onPolygonsChange?.([...(polygons || []), path]);
  };

  useDrawingMode(
    isLoaded,
    map,
    isDrawingMode && canDrawMore,
    handlePolygonComplete,
    setIsDrawingMode,
    { stayActive: true }
  );

  // --- Limpiar todo (reinicia el estado: permite volver a dibujar)
  const clearPolygons = () => {
    onPolygonsChange?.([]);
  };

  const clampedRadius = Math.min(
    MAX_RADIUS,
    Math.max(MIN_RADIUS, Number(radius) || DEFAULT_RADIUS),
  );

  // Si el radio viene fuera de rango (sesiones viejas con >1500 m), lo corregimos.
  useEffect(() => {
    if (Number(radius) !== clampedRadius) onRadiusChange?.(clampedRadius);
  }, [radius, clampedRadius, onRadiusChange]);

  // --- Cambiar modo
  const switchMode = (mode) => {
    if (mode === searchMode) return;
    if (mode === "radius") {
      // Si venía dibujando, cerramos el modo dibujo pero NO borramos polígonos.
      setIsDrawingMode(false);
      // Asegurar un radius razonable si nunca se tocó el slider.
      if (!radius || radius < MIN_RADIUS || radius > MAX_RADIUS) {
        onRadiusChange?.(DEFAULT_RADIUS);
      }
    }
    onModeChange?.(mode);
  };

  // --- Círculo del radio (solo en modo radius)
  useEffect(() => {
    if (!center || !isLoaded) return;
    if (searchMode !== "radius") {
      setCirclePath([]);
      return;
    }
    const c = new window.google.maps.LatLng(center.lat, center.lng);
    const coords = generateCircleCoords(c, clampedRadius);
    const path = coords.map((pt) => ({ lat: pt.lat(), lng: pt.lng() }));
    setCirclePath(path);
    if (map && path.length) {
      const bounds = new window.google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds);
    }
  }, [center, clampedRadius, isLoaded, map, searchMode]);

  // --- Fit a polygons cuando hay alguno
  useEffect(() => {
    if (!isLoaded || !map || !polygons.length) return;
    if (searchMode !== "polygons") return;
    const bounds = new window.google.maps.LatLngBounds();
    polygons.forEach((path) =>
      path.forEach((pt) => bounds.extend({ lat: Number(pt.lat), lng: Number(pt.lng) }))
    );
    if (!bounds.isEmpty()) map.fitBounds(bounds);
  }, [isLoaded, map, searchMode, polygons]);

  // --- Query params para /count y /search según el modo activo
  const buildSearchParams = () => {
    const params = new URLSearchParams();
    if (typeProperty) params.set("type_property", typeProperty);
    if (operationType) params.set("type_operation", operationType);
    if (searchMode === "polygons" && polygons.length > 0) {
      params.set("polygon", JSON.stringify(polygons));
    } else if (searchMode === "radius" && center) {
      params.set("lat", center.lat);
      params.set("lng", center.lng);
      params.set("radius", radius);
    } else {
      return null;
    }
    return params;
  };

  // --- Count (debounced)
  useEffect(() => {
    let mounted = true;
    const params = buildSearchParams();
    if (!params) {
      setCount(null);
      onCountChange?.(0);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setLoadingCount(true);
        const res = await fetch(`${apiUrl}/properties/count?${params.toString()}`);
        const data = await res.json();
        if (mounted) {
          setCount(data.count ?? 0);
          onCountChange?.(data.count ?? 0);
        }
      } catch (err) {
        console.error("Error fetching count", err);
        if (mounted) setCount(0);
      } finally {
        if (mounted) setLoadingCount(false);
      }
    }, 350);
    return () => {
      mounted = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lng, radius, typeProperty, operationType, apiUrl, searchMode, JSON.stringify(polygons)]);

  // --- Markers de propiedades (sampled)
  useEffect(() => {
    let mounted = true;
    const params = buildSearchParams();
    if (!params) {
      setPropertiesList([]);
      return;
    }
    params.set("limit", 50);
    const t = setTimeout(async () => {
      try {
        setLoadingProps(true);
        const res = await fetch(`${apiUrl}/properties/search?${params.toString()}`);
        const data = await res.json();
        if (mounted) setPropertiesList(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching properties", err);
        if (mounted) setPropertiesList([]);
      } finally {
        if (mounted) setLoadingProps(false);
      }
    }, 500);
    return () => {
      mounted = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lng, radius, typeProperty, operationType, apiUrl, searchMode, JSON.stringify(polygons)]);

  if (loadError) return <div>Error cargando el mapa</div>;
  if (!isLoaded) return <div>Cargando mapa...</div>;

  const drawBtnDisabledTitle = !canDrawMore
    ? "Cambiaste a modo radio. Presioná 'Limpiar todo' para volver a dibujar."
    : undefined;

  const statsFooter = (
    <div className="map-preview-footer">
      <div className="map-preview-stats">
        {searchMode === "radius" ? (
          <div className="map-preview-stat">
            <span className="map-preview-stat__label">Radio</span>
            <span className="map-preview-stat__value">{clampedRadius} m</span>
          </div>
        ) : (
          <div className="map-preview-stat">
            <span className="map-preview-stat__label">Zonas</span>
            <span className="map-preview-stat__value">{polygons.length}</span>
          </div>
        )}
        <div className="map-preview-stat">
          <span className="map-preview-stat__label">Tipo</span>
          <span className="map-preview-stat__value">{typeProperty || "Todos"}</span>
        </div>
        <div className="map-preview-stat">
          <span className="map-preview-stat__label">Operación</span>
          <span className="map-preview-stat__value">
            {operationType === "alquiler-temporal"
              ? "Alquiler temporal"
              : operationType || "Todos"}
          </span>
        </div>
        <div className="map-preview-stat">
          <span className="map-preview-stat__label">En la zona</span>
          <span className="map-preview-stat__value">
            {loadingCount ? "…" : count ?? "-"}
          </span>
        </div>
      </div>
      {!embedded && (
        <button
          type="button"
          className="map-preview-accept"
          onClick={onClose}
          disabled={searchMode === "polygons" && polygons.length === 0}
        >
          Aceptar
        </button>
      )}
    </div>
  );

  return (
    <div className={`map-preview-modal${embedded ? " map-preview-modal--embedded" : ""}`}>
      <div className="map-preview-card">
        {!embedded && (
          <div className="map-preview-header">
            <h3>Vista previa de zona</h3>
            <button className="close-button" onClick={onClose}>✕</button>
          </div>
        )}

        <div className={embedded ? "map-preview-split" : "map-preview-stack"}>
        <div className="map-preview-side">
        {/* ELECCIÓN DE MODO */}
        <div className="mode-picker">
          <div className="mode-picker-label">Método de búsqueda</div>
          <div className="mode-picker-buttons" role="group" aria-label="Método de búsqueda">
            <button
              type="button"
              className={`mode-btn ${searchMode === "radius" ? "active" : ""}`}
              onClick={() => switchMode("radius")}
            >
              <i className="pi pi-circle" aria-hidden="true" />
              <div>
                <div className="mode-btn-title">Por radio</div>
                <div className="mode-btn-sub">Círculo alrededor de la ubicación</div>
              </div>
            </button>
            <button
              type="button"
              className={`mode-btn ${searchMode === "polygons" ? "active" : ""}`}
              onClick={() => switchMode("polygons")}
            >
              <i className="pi pi-pencil" aria-hidden="true" />
              <div>
                <div className="mode-btn-title">Dibujar zonas</div>
                <div className="mode-btn-sub">Una o varias áreas a mano</div>
              </div>
            </button>
          </div>
        </div>

        {/* Controles del modo activo */}
        {searchMode === "radius" && (
          <div className="mode-controls">
            <div className="mode-controls__label-row">
              <label className="mode-controls__label" htmlFor="wizard-radius-slider">
                Radio
              </label>
              <span className="mode-controls__value">{clampedRadius} m</span>
            </div>
            <input
              id="wizard-radius-slider"
              type="range"
              className="radius-slider"
              min={MIN_RADIUS}
              max={MAX_RADIUS}
              step={50}
              value={clampedRadius}
              onChange={(e) => onRadiusChange?.(Number(e.target.value))}
            />
            {hasPolygons && (
              <p className="mode-controls__hint">
                Tenés {polygons.length} zona{polygons.length > 1 ? "s" : ""} dibujada
                {polygons.length > 1 ? "s" : ""} guardada{polygons.length > 1 ? "s" : ""}; no se usan en modo radio.
              </p>
            )}
          </div>
        )}

        {searchMode === "polygons" && (
          <div className="mode-controls mode-controls--polygons">
            <div className="mode-controls__tip" role="note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              <span>
                {POLYGON_PRECISION_TIP} Mantené presionado y arrastrá sobre el mapa para dibujar.
              </span>
            </div>
            <div className="mode-controls__actions">
              <Button
                icon={isDrawingMode ? "pi pi-times" : "pi pi-pencil"}
                label={isDrawingMode ? "Cancelar dibujo" : "Dibujar polígono"}
                className="p-button-sm polygon-draw-btn"
                onClick={() => setIsDrawingMode((v) => !v)}
                disabled={!canDrawMore}
                title={drawBtnDisabledTitle}
              />
              <Button
                icon="pi pi-trash"
                label="Limpiar todo"
                className="p-button-sm polygon-clear-btn"
                onClick={clearPolygons}
                disabled={!hasPolygons}
              />
            </div>
            {hasPolygons ? (
              <p className="mode-controls__hint">
                {`${polygons.length} zona${polygons.length > 1 ? "s" : ""} dibujada${polygons.length > 1 ? "s" : ""}`}
              </p>
            ) : null}
          </div>
        )}
        {embedded ? statsFooter : null}
        </div>

        <div className="map-preview-body">
          <GoogleMap
            mapContainerStyle={embedded ? mapContainerStyleEmbedded : mapContainerStyle}
            center={center}
            zoom={14}
            onLoad={(m) => setMap(m)}
            options={{ fullscreenControl: false, streetViewControl: false }}
          >
            {center && searchMode === "radius" && (
              <Marker position={{ lat: Number(center.lat), lng: Number(center.lng) }} />
            )}

            {searchMode === "radius" && circlePath.length > 0 && (
              <Polygon
                paths={circlePath}
                options={{
                  strokeColor: "#FA7317",
                  strokeOpacity: 0.7,
                  strokeWeight: 2,
                  fillColor: "#FA7317",
                  fillOpacity: 0.12,
                }}
              />
            )}

            {/* Polígonos dibujados: se muestran siempre que existan.
                Si estamos en modo radio, se ven atenuados. */}
            {hasPolygons &&
              polygons.map((path, idx) => (
                <Polygon
                  key={idx}
                  paths={path.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }))}
                  options={
                    searchMode === "polygons"
                      ? {
                          strokeColor: "#FF0000",
                          strokeOpacity: 0.7,
                          strokeWeight: 2,
                          fillColor: "#FF0000",
                          fillOpacity: 0.12,
                        }
                      : {
                          strokeColor: "#999",
                          strokeOpacity: 0.5,
                          strokeWeight: 1,
                          fillColor: "#999",
                          fillOpacity: 0.08,
                          clickable: false,
                        }
                  }
                />
              ))}

            {propertiesList.map((p, i) => {
              const lat = Number(p.lat ?? p.latitude ?? p.latitude_value ?? 0);
              const lng = Number(p.lng ?? p.longitude ?? p.longitude_value ?? 0);
              if (!lat || !lng) return null;
              return <Marker key={p.property_group_key ?? p.id ?? p.url ?? i} position={{ lat, lng }}
                icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: portalColors(p)[0], fillOpacity: 1, strokeColor: portalColors(p)[1] || '#ffffff', strokeWeight: 2 }} />;
            })}
          </GoogleMap>

          {!embedded ? statsFooter : null}
        </div>
        </div>
      </div>

      <style>{`
        .map-preview-modal {
          position: fixed; inset: 0;
          display: flex; align-items: center; justify-content: center;
          background: rgba(0,0,0,0.35); z-index: 2000;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          padding: 16px;
        }
        .map-preview-modal--embedded {
          position: static;
          inset: auto;
          display: flex;
          flex-direction: column;
          flex: none;
          min-height: 0;
          background: transparent;
          z-index: auto;
          overflow: visible;
          padding: 0;
          width: 100%;
        }
        .map-preview-card {
          width: min(900px, 100%);
          max-height: calc(100vh - 32px);
          background: #fff; border-radius: 12px;
          padding: 12px;
          box-shadow: 0 6px 24px rgba(0,0,0,0.2);
          display: flex; flex-direction: column;
          overflow-y: auto;
        }
        .map-preview-modal--embedded .map-preview-card {
          width: 100%;
          flex: none;
          min-height: 0;
          max-height: none;
          box-shadow: none;
          border: 1px solid rgba(15, 23, 42, 0.08);
          background: rgba(255, 255, 255, 0.55);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 14px;
          padding: 14px;
          overflow: visible;
        }
        .map-preview-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px }
        .close-button { background: transparent; border: none; font-size:18px; cursor:pointer; padding: 4px 8px }
        .map-preview-stack,
        .map-preview-side {
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-width: 0;
        }
        .map-preview-split {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 16px;
          align-items: stretch;
          flex: none;
          min-height: 420px;
          height: auto;
          overflow: visible;
        }
        .map-preview-split .map-preview-side {
          overflow: visible;
          min-height: 0;
        }
        .map-preview-split .mode-picker {
          margin-bottom: 0;
        }
        .map-preview-split .mode-picker-buttons {
          grid-template-columns: 1fr 1fr;
        }
        .map-preview-split .map-preview-stats {
          grid-template-columns: 1fr 1fr;
        }
        .map-preview-split .map-preview-footer {
          margin-top: 0;
        }
        .map-preview-split .mode-controls--polygons .mode-controls__actions > .p-button {
          justify-content: center;
        }
        .map-preview-body { display:flex; flex-direction:column; gap:10px; min-width: 0; min-height: 0; }
        .map-preview-split .map-preview-body {
          height: 100%;
        }
        .map-preview-body > div:first-child {
          border-radius: 12px;
          overflow: hidden;
          flex: 1;
          min-height: 0;
        }
        .map-preview-split .map-preview-body > div:first-child {
          height: 100%;
          min-height: 420px;
        }

        .mode-picker { margin: 0 0 12px; }
        .mode-picker-label {
          display: block;
          margin-bottom: 0.45rem;
          font-size: 0.78rem;
          color: #64748b;
          font-weight: 650;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .mode-picker-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 0.65rem; }
        .mode-btn {
          display: flex; align-items: center; gap: 12px;
          min-height: 64px;
          padding: 0.85rem 1rem;
          border: 1px solid rgba(250, 115, 23, 0.16);
          border-radius: 12px;
          background: linear-gradient(155deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 247, 237, 0.7) 100%);
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
          cursor: pointer; text-align: left; font-family: inherit;
          transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
          width: 100%;
        }
        .mode-btn:hover { border-color: rgba(250, 115, 23, 0.45); }
        .mode-btn.active {
          background: rgba(250, 115, 23, 0.1);
          border-color: #FA7317;
          box-shadow: 0 0 0 3px rgba(250,115,23,0.12);
        }
        .mode-btn i { font-size: 1.05rem; color: #c2410c; flex-shrink: 0; }
        .mode-btn-title { font-weight: 650; color: #0f172a; font-size: 0.95rem; line-height: 1.2; }
        .mode-btn.active .mode-btn-title { color: #c2410c; }
        .mode-btn-sub { font-size: 0.78rem; color: #64748b; margin-top: 2px; line-height: 1.3; }

        .mode-controls {
          margin-bottom: 12px;
          padding: 0.85rem 0.95rem;
          background: rgba(248, 250, 252, 0.8);
          border: 1px solid rgba(15, 23, 42, 0.06);
          border-radius: 12px;
        }
        .mode-controls__label-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          margin-bottom: 0.55rem;
        }
        .mode-controls__label {
          margin: 0;
          font-size: 0.78rem;
          color: #64748b;
          font-weight: 650;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .mode-controls__value {
          font-size: 0.92rem;
          font-weight: 700;
          color: #c2410c;
          font-variant-numeric: tabular-nums;
        }
        .mode-controls__hint {
          margin: 0.55rem 0 0;
          font-size: 0.8rem;
          line-height: 1.4;
          color: #64748b;
        }
        .mode-controls__tip {
          display: flex;
          align-items: flex-start;
          gap: 0.55rem;
          margin: 0 0 0.75rem;
          padding: 0.7rem 0.8rem;
          border-radius: 10px;
          background: rgba(250, 115, 23, 0.08);
          border: 1px solid rgba(250, 115, 23, 0.2);
          color: #9a3412;
          font-size: 0.8rem;
          line-height: 1.45;
        }
        .mode-controls__tip i {
          margin-top: 0.1rem;
          font-size: 0.9rem;
          color: #fa7317;
          flex-shrink: 0;
        }
        .mode-controls--polygons {
          background: linear-gradient(155deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 247, 237, 0.7) 100%);
          border: 1px solid rgba(250, 115, 23, 0.16);
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
        }
        .mode-controls--polygons .mode-controls__actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.65rem;
          align-items: center;
        }
        .mode-controls--polygons .polygon-draw-btn.p-button {
          background: linear-gradient(135deg, #fa7317 0%, #c2410c 100%);
          border: none;
          color: #fff;
          border-radius: 12px;
          min-height: 44px;
          padding: 0.7rem 1.2rem;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(250, 115, 23, 0.25);
        }
        .mode-controls--polygons .polygon-draw-btn.p-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #fa7317 0%, #c2410c 100%);
          color: #fff;
          border: none;
          box-shadow: 0 6px 18px rgba(250, 115, 23, 0.35);
        }
        .mode-controls--polygons .polygon-clear-btn.p-button {
          background: #fff;
          border: 1px solid rgba(250, 115, 23, 0.35);
          color: #c2410c;
          border-radius: 12px;
          min-height: 44px;
          padding: 0.7rem 1.2rem;
          font-weight: 600;
          box-shadow: none;
        }
        .mode-controls--polygons .polygon-clear-btn.p-button:hover:not(:disabled) {
          background: rgba(250, 115, 23, 0.08);
          border-color: #fa7317;
          color: #c2410c;
        }
        .mode-controls--polygons .polygon-draw-btn.p-button:disabled,
        .mode-controls--polygons .polygon-clear-btn.p-button:disabled {
          opacity: 0.55;
          box-shadow: none;
        }

        .radius-slider {
          width: 100%;
          accent-color: #FA7317;
          height: 8px;
          cursor: pointer;
        }
        .radius-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #FA7317;
          cursor: pointer;
          border: 2px solid #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        }
        .radius-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #FA7317;
          cursor: pointer;
          border: 2px solid #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        }

        .map-preview-footer {
          margin-top: 4px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 16px;
        }
        .map-preview-stats {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
          flex: 1;
        }
        .map-preview-stat {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
          padding: 0.55rem 0.65rem;
          border-radius: 10px;
          background: rgba(248, 250, 252, 0.85);
          border: 1px solid rgba(15, 23, 42, 0.05);
        }
        .map-preview-stat__label {
          font-size: 0.68rem;
          font-weight: 650;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: #64748b;
        }
        .map-preview-stat__value {
          font-size: 0.88rem;
          font-weight: 700;
          color: #0f172a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .map-preview-accept {
          background-color: #FA7317;
          color: #fff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          padding: 10px 24px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          flex-shrink: 0;
          font-family: inherit;
        }
        .map-preview-accept:hover:not(:disabled) {
          background-color: #c2410c;
        }
        .map-preview-accept:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-height: 720px) {
          .map-preview-modal--embedded .map-preview-card {
            padding: 10px;
          }
          .map-preview-split {
            gap: 10px;
          }
          .mode-picker { margin-bottom: 8px; }
          .mode-picker-label { margin-bottom: 0.3rem; }
          .mode-btn {
            min-height: 46px;
            gap: 8px;
            padding: 0.45rem 0.7rem;
          }
          .mode-btn-sub { display: none; }
          .mode-controls {
            margin-bottom: 8px;
            padding: 0.55rem 0.7rem;
          }
          .mode-controls__label-row { margin-bottom: 0.35rem; }
          .map-preview-stat { padding: 0.35rem 0.5rem; }
          .map-preview-footer { margin-top: 0; }
        }

        @media (max-width: 768px) {
          .map-preview-modal { padding: 0; }
          .map-preview-modal:not(.map-preview-modal--embedded) .map-preview-card {
            width: 100%;
            max-height: 100dvh;
            height: 100dvh;
            border-radius: 0;
            padding: 10px;
          }
          .map-preview-modal--embedded .map-preview-card {
            padding: 10px;
            border-radius: 12px;
          }
          .map-preview-split {
            grid-template-columns: 1fr;
            height: auto;
            min-height: 0;
          }
          .map-preview-split .map-preview-body > div:first-child {
            height: 280px;
          }
          .mode-picker-buttons { grid-template-columns: 1fr; gap: 0.55rem; }
          .mode-btn { min-height: 56px; padding: 0.75rem 0.85rem; }
          .map-preview-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .map-preview-footer { flex-direction: column; align-items: stretch; }
          .map-preview-accept { width: 100%; text-align: center; }
          .mode-controls--polygons .mode-controls__actions > .p-button {
            flex: 1 1 auto;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .map-preview-modal--embedded .gm-style,
          .map-preview-modal--embedded .map-preview-body > div:first-child {
            min-height: 260px;
          }
        }
      `}</style>
    </div>
  );
}
